import React from "react";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import {
  DeleteItemCommand,
  DynamoDBClient,
  PutItemCommand,
  UpdateItemCommand,
  QueryCommand,
  ScanCommand,
} from "@aws-sdk/client-dynamodb";
import {
  PutObjectCommand,
  DeleteObjectCommand,
  DeleteObjectsCommand,
  ListObjectsCommand,
} from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { useEffect, useState } from "react";
import ResponsiveAppBar from "./components/AppBar";
import { publishOgPage } from "./ogPage";
import { invalidateAlbum } from "./albumPhotos";
import {
  applyPhotoOrder,
  photoOrderAttribute,
  readPhotoOrder,
} from "./photoOrder";
import { dayStart, startOfToday } from "./utils/dates";
import { priceBound } from "./public/utils/format";
import { phoneMatches } from "./utils/phone";
import { CHRISTMAS, HALLOWEEN } from "./public/halloween/season";
import HalloweenOverlay from "./public/halloween/HalloweenOverlay";
import { Pumpkin } from "./public/halloween/sprites";
import Snowfall from "./public/christmas/Snowfall";
import Sprite from "./public/christmas/Sprite";
import { ICONS } from "./public/christmas/atlas";
import RealEstateObjectsPage from "./pages/RealEstateObjectsPage";
import BuildingInfo from "./dialogs/Info";
import StandardImageList from "./dialogs/StandardImageList";
import ClientsPage from "./pages/ClientsPage";
import "./index.css";
import { S3Client } from "@aws-sdk/client-s3";
import ArchivesPage from "./pages/ArchivesPage";
import WatermarkPage from "./pages/WatermarkPage";
import BeforeDeleteObject from "./dialogs/BeforeDeleteObject";
import BeforeDeleteArchive from "./dialogs/BeforeDeleteArchive";
import BeforeReturnArchive from "./dialogs/BeforeReturnArchive";
import {
  S3_PHOTOS_BUCKET,
  makeThumbnail,
  thumbKey,
  photoUrl,
  thumbUrl,
  albumPrefixes,
  PHOTO_CACHE_CONTROL,
} from "./media";

window.Buffer = window.Buffer || require("buffer").Buffer;

// const USER_POOL_ID = "eu-central-1_O4YAKLH4N";
// const USER_POOL_PROVIDER = "cognito-idp." + REGION + ".amazonaws.com/" + USER_POOL_ID;
const PRODUCTION = !window.location.href.includes("localhost");

/**
 * Cognito Hosted UI. `redirect_uri` must exactly match a callback URL
 * registered on the app client — it points at the site root, and App.js
 * forwards the token on to /admin.
 */
const COGNITO_CLIENT_ID = "806nes7b338tt5cue0ivet5mg";
const COGNITO_DOMAIN = "https://auth.leocity.estate";
const COGNITO_REDIRECT_URI = PRODUCTION
  ? "https://leocity.estate/"
  : "http://localhost:3000/";

const LOGIN_URL =
  COGNITO_DOMAIN +
  "/login?client_id=" +
  COGNITO_CLIENT_ID +
  "&response_type=token&scope=email+openid+profile&redirect_uri=" +
  encodeURIComponent(COGNITO_REDIRECT_URI);

const TABLE_NAME_OBJECTS = "objects_v3";
const TABLE_NAME_CLIENTS = "clients_v2";
const TABLE_NAME_ARCHIVES = "archives_v2";
const TABLE_NAME_DELETED_ARCHIVE = "deleted_archives";
const REGION = "eu-central-1";
const IDENTITY_POOL_ID = "eu-central-1:713c28fd-d5fa-4e35-a9de-b89640b12b65";
const OBJECTS_PROJECTION =
  "id, cost, currency, dateFrom, dateTo, floor, furniture, heating, info, personName, phoneNumber, region, rooms, state, street, technology";
const CLIENTS_PROJECTION =
  "id, cost, aboutClient, currency, dateFrom, deadline, info, personName, phoneNumber, region, rooms, urgency";
const SCAN_LIMIT = 100;

var ddbClient;
var s3Client;
var idToken;
var admin = null;
var openedWindow;

function signIn(idToken) {
  if (ddbClient != null) {
    return;
  }

  /**
   * Always exchange the Cognito token — on localhost too.
   *
   * This used to fall back to unauthenticated (guest) credentials during local
   * development, which worked only because the guest role had blanket DynamoDB
   * access. Now that the guest role is scoped to public reads, an
   * unauthenticated panel fails on its first Query with AccessDeniedException
   * on `objects_v3/index/global-dateEdit-index`. Logging in locally is both the
   * fix and the more honest test: dev now exercises the same role as production.
   */
  var credentials = fromCognitoIdentityPool({
    client: new CognitoIdentityClient({ region: REGION }),
    identityPoolId: IDENTITY_POOL_ID,
    logins: {
      "cognito-idp.eu-central-1.amazonaws.com/eu-central-1_O4YAKLH4N": idToken,
    },
  });

  ddbClient = new DynamoDBClient({
    region: REGION,
    credentials: credentials,
  });

  s3Client = new S3Client({
    region: REGION,
    credentials: credentials,
  });
}

//  const putItem = async () => {
//     try {
//       const data = await ddbClient.send(new PutItemCommand({
//         TableName: TABLE_NAME,
//         Item:{
//           "id": {S: "test123"}
//         }
//       }));
//       console.error("success");
//       return data;
//     } catch (err) {
//       console.error(err);
//     }
//   };

// The phone the archives are being searched by. Phones are matched in the
// browser (see utils/phone.js), page by page — and the follow-up pages are
// fetched with the search fields blanked out, so it has to be remembered.
var lastPhoneArchives = "";
var lastQueryPropertiesObjects,
  lastQueryPropertiesClients,
  lastQueryPropertiesArchives;
var lastEvaluatedKeyObjects, lastEvaluatedKeyClients, lastEvaluatedKeyArchives;

var clientRegionSearchTerms = [];
var regionSearchTerms = [];
var streetSearchTerms = [];

function timeout(delay) {
  return new Promise((res) => setTimeout(res, delay));
}

function AppRealtor() {
  const [displayAdminTools, setDisplayAdminTools] = useState(false);
  const [activeItemId, setActiveItemId] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [openDeleteArchiveDialog, setOpenDeleteArchiveDialog] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showAddRealEstateObject, setShowAddRealEstateObject] = useState(false);
  const [realEstateItems, setRealEstateItems] = useState([]);
  const [showAddClients, setShowAddClients] = useState(false);
  const [clientItems, setClientItems] = useState([]);
  const [archiveItems, setArchiveItems] = useState([]);
  // Whether DynamoDB has another page beyond the rows in hand. Mirrors the
  // lastEvaluatedKey* vars above, which are not state and so cannot tell the
  // card view when to stop offering «load more».
  const [hasMoreObjects, setHasMoreObjects] = useState(false);
  const [hasMoreArchives, setHasMoreArchives] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [infoText, setInfoText] = useState("");
  const [openPhotos, setOpenPhotos] = useState(false);
  // Which listing the photos dialog is showing, so a reorder knows what to save.
  const [photosId, setPhotosId] = useState("");
  // The edit dialog shows the same album; it loads its own copy because it can
  // be opened without the photos dialog ever having been.
  const [editPhotos, setEditPhotos] = useState([]);
  const [editPhotosLoading, setEditPhotosLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [isEditItem, setIsEditItem] = useState(false);
  const [editItemId, setEditItemId] = useState("");
  const [currentPage, setCurrentPage] = useState("Objects");
  const [editItem, setEditItem] = useState({
    id: "",
    personName: "",
    phoneNumber: "",
    rooms: "",
    dateFrom: "",
    dateTo: "",
    cost: "",
    currency: "",
    street: "",
    region: "",
    floor: "",
    state: "",
    heating: "",
    info: "",
    furniture: false,
    technology: false,
  });
  const [editClientItem, setEditClientItem] = useState({
    id: "",
    dateFrom: "",
    rooms: "",
    cost: "",
    currency: "",
    region: "",
    aboutClient: "",
    deadline: "",
    urgency: "",
    personName: "",
    phoneNumber: "",
    info: "",
  });
  const [editArchiveItem, setEditArchiveItem] = useState(null);

  const handleOpenInfo = (infoId) => {
    realEstateItems.map((item) => {
      if (item.id === infoId) setInfoText(item.info);
    });
    setOpenInfo(true);
  };
  const handleOpenClientInfo = (infoId) => {
    clientItems.map((item) => {
      if (item.id === infoId) setInfoText(item.info);
    });
    setOpenInfo(true);
  };

  const openArchiveInfo = (infoId) => {
    archiveItems.map((item) => {
      if (item.id === infoId) setInfoText(item.info);
    });
    setOpenInfo(true);
  };

  /**
   * Uploads each picked file twice: the original under "<id>/name", and a
   * ~640px JPEG under "thumbs/<id>/name" that the public cards and photo
   * grids use instead of the full-size image.
   *
   * The thumbnail is best-effort — if the browser cannot produce one the
   * original still uploads, and the site falls back to it automatically.
   */
  const uploadPhotos = async (objectId, inputFiles) => {
    if (inputFiles == null || inputFiles.length === 0) return;

    const albumPhotosKey = encodeURIComponent(objectId) + "/";
    const tasks = [];

    for (var i = 0; i < inputFiles.length; i++) {
      const file = inputFiles[i];
      const key = albumPhotosKey + file.name;

      tasks.push(
        s3Client.send(
          new PutObjectCommand({
            Bucket: S3_PHOTOS_BUCKET,
            Key: key,
            Body: file,
            ContentType: file.type || "image/jpeg",
            CacheControl: PHOTO_CACHE_CONTROL,
          })
        )
      );

      tasks.push(
        makeThumbnail(file)
          .then(function (blob) {
            if (!blob) return null;
            return s3Client.send(
              new PutObjectCommand({
                Bucket: S3_PHOTOS_BUCKET,
                Key: thumbKey(key),
                Body: blob,
                ContentType: "image/jpeg",
                CacheControl: PHOTO_CACHE_CONTROL,
              })
            );
          })
          .catch(function (err) {
            // Never fail the upload over a thumbnail.
            console.warn("Не вдалося створити мініатюру для " + file.name, err);
            return null;
          })
      );
    }

    await Promise.all(tasks);
    // The card view caches each listing's album to find its cover; without this
    // a listing that just got its first photo keeps showing "Без фото".
    invalidateAlbum(objectId);
    console.log("photo upload success");
  };

  const onDeletePhoto = async (photo) => {
    console.log(`Delete: ${photo.key}`);
    // setShowLoading(true);
    try {
      await s3Client.send(
        new DeleteObjectCommand({
          Key: photo.key,
          Bucket: S3_PHOTOS_BUCKET,
        })
      );
      // The thumbnail lives under a parallel prefix; an older photo may not
      // have one yet, so a failure here is not an error worth surfacing.
      try {
        await s3Client.send(
          new DeleteObjectCommand({
            Key: thumbKey(photo.key),
            Bucket: S3_PHOTOS_BUCKET,
          })
        );
      } catch (thumbErr) {
        console.warn("Мініатюру не видалено (можливо, її й не було)", thumbErr);
      }
      console.log("delete success");
      // Deleting the first photo changes the card's cover.
      invalidateAlbum(photo.id);
      // Both dialogs may be showing this album.
      setPhotos(photos.filter((item) => item.key !== photo.key));
      setEditPhotos((current) =>
        current.filter((item) => item.key !== photo.key)
      );
    } catch (err) {
      alert(err);
      console.error(err);
    }
    // setShowLoading(false);
  };

  /** The listing with this id, whichever list it is currently in. */
  const findListing = (id) => {
    var found = realEstateItems.filter((entry) => entry.id === id)[0];
    if (found) return found;
    return archiveItems.filter((entry) => entry.id === id)[0] || null;
  };

  const isArchived = (id) =>
    !realEstateItems.some((entry) => entry.id === id) &&
    archiveItems.some((entry) => entry.id === id);

  const handleOpenPhotos = async (photosId) => {
    //...
    //after loaded photos
    const albumPhotosKey = encodeURIComponent(photosId) + "/";
    try {
      var photos = [];

      const data = await s3Client.send(
        new ListObjectsCommand({
          Prefix: albumPhotosKey,
          Bucket: S3_PHOTOS_BUCKET,
        })
      );

      if (data.Contents == null || data.Contents.length < 1) {
        alert("Немає фото.");
        return;
      }
      data.Contents.map((photo) => {
        photos.push({
          url: photoUrl(photo.Key),
          thumbUrl: thumbUrl(photo.Key),
          name: photo.Key.split("/").pop(),
          id: photosId,
          key: photo.Key,
        });
      });

      const listing = findListing(photosId);
      setPhotos(applyPhotoOrder(photos, listing ? listing.photoOrder : []));
      setPhotosId(photosId);
      setOpenPhotos(true);
    } catch (err) {
      alert(err);
      console.error(err);
    }
  };

  const getPhotos = async (photosId) => {
    //...
    //after loaded photos
    const albumPhotosKey = encodeURIComponent(photosId) + "/";
    try {
      var photos = [];

      const data = await s3Client.send(
        new ListObjectsCommand({
          Prefix: albumPhotosKey,
          Bucket: S3_PHOTOS_BUCKET,
        })
      );

      // No photos yet means no Contents at all, not an empty list.
      (data.Contents || []).map((photo) => {
        photos.push({
          url: photoUrl(photo.Key),
          thumbUrl: thumbUrl(photo.Key),
          name: photo.Key.split("/").pop(),
          id: photosId,
          key: photo.Key,
        });
      });

      return photos;
    } catch (err) {
      alert(err);
      console.error(err);
    }
  };

  const handleEditItem = (id) => {
    setIsEditItem(true);
    setEditItemId(id);
    realEstateItems.map((item) => {
      if (item.id === id) setEditItem(item);
    });
    loadEditAlbum(id);
  };

  /** The listing's existing photos, for the grid inside the edit dialog. */
  const loadEditAlbum = async (id) => {
    setEditPhotos([]);
    setEditPhotosLoading(true);
    try {
      const found = await getPhotos(id);
      const listing = findListing(id);
      setEditPhotos(
        applyPhotoOrder(found || [], listing ? listing.photoOrder : [])
      );
    } catch (err) {
      console.error(err);
    }
    setEditPhotosLoading(false);
  };

  /**
   * Save a new photo order for one listing.
   *
   * Only `photoOrder` is written — an UpdateItem, not a Put — so nothing else
   * about the listing can be disturbed by reordering its pictures.
   *
   * When the first photo changes, the share preview is rebuilt too: the og page
   * names a specific image file, so without this a link shared after a reorder
   * would still show the old cover. Telegram refetches; Viber caches on the
   * sender's device, so a link already sent keeps its old picture.
   */
  const reorderPhotos = async (objectId, nextPhotos) => {
    if (!ddbClient || !objectId || !nextPhotos) return;

    const archived = isArchived(objectId);
    const listing = findListing(objectId);
    const previousCover =
      listing && listing.photoOrder && listing.photoOrder.length > 0
        ? listing.photoOrder[0]
        : null;
    const names = nextPhotos.map((photo) => photo.name);

    // Paint the new order immediately; the write is small but not instant.
    if (openPhotos && photosId === objectId) setPhotos(nextPhotos);
    setEditPhotos((current) =>
      current.length > 0 && current[0] && current[0].id === objectId
        ? nextPhotos
        : current
    );

    const apply = (entry) =>
      entry.id === objectId ? { ...entry, photoOrder: names } : entry;
    setRealEstateItems((current) => current.map(apply));
    setArchiveItems((current) => current.map(apply));

    try {
      await ddbClient.send(
        new UpdateItemCommand({
          TableName: archived ? TABLE_NAME_ARCHIVES : TABLE_NAME_OBJECTS,
          Key: { id: { S: objectId } },
          UpdateExpression: "SET photoOrder = :order",
          ExpressionAttributeValues: {
            ":order": photoOrderAttribute(names) || { L: [] },
          },
        })
      );
    } catch (err) {
      alert("Не вдалося зберегти порядок фото: " + err.message);
      console.error(err);
      return;
    }

    // The card view caches the album to find its cover.
    invalidateAlbum(objectId);

    if (!archived && names[0] !== previousCover && listing) {
      try {
        await publishOgPage(s3Client, { ...listing, photoOrder: names });
      } catch (ogErr) {
        console.warn(
          "Порядок збережено, але сторінку прев'ю не оновлено. Див. SHARE_PREVIEW.md.",
          ogErr
        );
      }
    }
  };
  const handleEditClientItem = (id) => {
    setIsEditItem(true);
    setEditItemId(id);
    clientItems.map((item) => {
      if (item.id === id) setEditClientItem(item);
    });
  };
  const handleEditArchiveItem = (id) => {
    setIsEditItem(true);
    setEditItemId(id);
    archiveItems.map((item) => {
      if (item.id === id) setEditArchiveItem(item);
    });
    loadEditAlbum(id);
  };

  const onTablePageChange = (pageNumber, details) => {
    console.log(`page: ${pageNumber}`);
  };

  if (ddbClient == null) {
    if (idToken == null) {
      var url_string = window.location.href.replace("#", "?");
      var url = new URL(url_string);
      idToken = url.searchParams.get("id_token");
    }

    if (idToken != null) {
      signIn(idToken);
    } else {
      // The panel is admin-only: no token means straight to the Hosted UI.
      // Localhost included — COGNITO_REDIRECT_URI above already points at
      // http://localhost:3000/ in that case, so the same flow works locally.
      console.info("Немає токена — перенаправлення на сторінку входу");
      window.open(LOGIN_URL, "_self");
    }
  }

  //check if admin
  const checkAdmin = async () => {
    if (ddbClient == null) return;

    // No localhost shortcut: the probe below is the only thing that decides
    // who is an admin, and it now runs identically in dev and production.
    try {
      await ddbClient.send(
        new DeleteItemCommand({
          TableName: TABLE_NAME_OBJECTS,
          Key: {
            id: { S: "admin" },
          },
        })
      );
      console.log("admin === true");
      admin = true;
      listObjects();
    } catch (err) {
      console.log(err);
      if (err.name === "AccessDeniedException") {
        console.log("admin === false");
        admin = false;
        listObjects();
      } else {
        // Expired or malformed token — send the user back through the login.
        console.warn("Невідома помилка доступу, повторний вхід: ", err);
        window.open(LOGIN_URL, "_self");
      }
    }
  };

  //old list objects
  // const listObjects = async (lastEvaluatedKey) => {
  //   if (ddbClient == null) {
  //     //alert("db client == null");
  //     return;
  //   }

  //   var loadNext = lastEvaluatedKey != null;

  //   if (loadNext && lastEvaluatedKey.lastEvaluatedKey == null) return;

  //   if (loadNext) console.log("load next: ", lastEvaluatedKey);
  //   else console.log("list objects");

  //   setShowLoading(true);

  //   try {
  //     const data = await ddbClient.send(
  //       new ScanCommand({
  //         TableName: TABLE_NAME_OBJECTS,
  //         Limit: SCAN_LIMIT,
  //         ExclusiveStartKey: loadNext
  //           ? lastEvaluatedKey.lastEvaluatedKey
  //           : null,
  //         // ExclusiveStartKey: { lastEvaluatedKey },
  //         // lastEvaluatedKey != null
  //         //   ? { id: { S: "595186Sun, 12 Jun 2022 10:22:27 GMT" } }
  //         //   : lastEvaluatedKey,
  //         // ProjectionExpression: OBJECTS_PROJECTION,
  //       })
  //     );

  //     lastQueryPropertiesObjects = null;
  //     lastEvaluatedKeyObjects = data.LastEvaluatedKey;

  //     console.log("lastEvaluatedKey: ", lastEvaluatedKeyObjects);

  //     var items = [];
  //     data.Items.forEach(function (element, index, array) {
  //       items.push({
  //         id: element.id.S,
  //         personName: element.personName.S,
  //         phoneNumber: element.phoneNumber.S,
  //         rooms: element.rooms.N,
  //         dateFrom: element.dateFrom.S,
  //         dateEdit: element.dateEdit === undefined ? "" : element.dateEdit.S,
  //         cost: element.cost.N,
  //         currency: element.currency.S,
  //         street: element.street.S,
  //         region: element.region.S,
  //         floor: element.floor.N,
  //         state: element.state.S,
  //         heating: element.heating.S,
  //         info: element.info.S,
  //         furniture: element.furniture.BOOL,
  //         technology: element.technology.BOOL,
  //       });
  //     });

  //     if (loadNext) setRealEstateItems([...realEstateItems, ...items]);
  //     else setRealEstateItems(items);

  //     items.map((item) => {
  //       if (streetSearchTerms.indexOf(item.street) === -1)
  //         streetSearchTerms.push(item.street);
  //       if (regionSearchTerms.indexOf(item.region) === -1)
  //         regionSearchTerms.push(item.region);
  //     });
  //   } catch (err) {
  //     alert(err);
  //     console.error(err);
  //   }
  //   setShowLoading(false);
  // };

  //new list objects
  const listObjects = async (loadNext = false, lastEvaluatedKey, prevItems) => {
    if (ddbClient == null) {
      //alert("db client == null");
      return;
    }
    ///test
    // setShowLoading(true);
    // console.log("scanning...");
    // const data = await ddbClient.send(
    //   new QueryCommand({
    //     TableName: "objects_v2",
    //     IndexName: "global-dateEdit-index",
    //     KeyConditionExpression: "#global = :global",
    //     ExpressionAttributeNames: {
    //       "#global": "global",
    //     },
    //     ExpressionAttributeValues: {
    //       ":global": { N: "0" },
    //     },
    //     Limit: 1000,
    //     ExclusiveStartKey: loadNext ? lastEvaluatedKey : null,
    //     ScanIndexForward: false,
    //   })
    // );
    // console.log("scanned: ", data.Items);
    // console.log("adding...");
    // var tasks = [];
    // data.Items.forEach(function(element, index, array) {
    //   // console.log(new Date(element.dateFrom.S).getTime());
    //   tasks.push(
    //     ddbClient.send(
    //       new PutItemCommand({
    //         TableName: TABLE_NAME_OBJECTS,
    //         Item: {
    //           id: element.id,
    //           global: { N: "0" },
    //           personName: element.personName,
    //           phoneNumber: element.phoneNumber,
    //           dateFrom: { N: `${new Date(element.dateFrom.S).getTime()}` },
    //           dateEdit: { N: `${new Date(element.dateEdit.S).getTime()}` },
    //           rooms: element.rooms,
    //           cost: element.cost,
    //           currency: element.currency,
    //           street: element.street,
    //           region: element.region,
    //           floor: element.floor,
    //           state: element.state,
    //           heating: element.heating,
    //           info: element.info,
    //           furniture: element.furniture,
    //           technology: element.technology,
    //         },
    //       })
    //     )
    //   );
    // });

    // await Promise.all(tasks);
    // console.log("added");

    // console.log("scanning...");
    // const data2 = await ddbClient.send(
    //   new QueryCommand({
    //     TableName: "archives",
    //     IndexName: "global-dateTerm-index",
    //     KeyConditionExpression: "#global = :global",
    //     ExpressionAttributeNames: {
    //       "#global": "global",
    //     },
    //     ExpressionAttributeValues: {
    //       ":global": { N: "0" },
    //     },
    //     Limit: 1000,
    //     ExclusiveStartKey: loadNext ? lastEvaluatedKey : null,
    //   })
    // );
    // console.log("scanned: ", data2.Items);
    // console.log("adding...");
    // var tasks = [];
    // data2.Items.forEach(function(element, index, array) {
    //   tasks.push(
    //     ddbClient.send(
    //       new PutItemCommand({
    //         TableName: TABLE_NAME_ARCHIVES,
    //         Item: {
    //           id: element.id,
    //           global: { N: "0" },
    //           personName: element.personName,
    //           phoneNumber: element.phoneNumber,
    //           dateFrom: { N: `${new Date(element.dateFrom.S).getTime()}` },
    //           dateDeleted: {
    //             N: `${new Date(element.dateDeleted.S).getTime()}`,
    //           },
    //           dateTerm: element.dateTerm,
    //           dateEdit: {
    //             N:
    //               element.dateEdit === undefined
    //                 ? "0"
    //                 : `${new Date(element.dateEdit.S).getTime()}`,
    //           },
    //           rooms: element.rooms,
    //           cost: element.cost,
    //           currency: element.currency,
    //           street: element.street,
    //           region: element.region,
    //           floor: element.floor,
    //           state: element.state,
    //           heating: element.heating,
    //           info: element.info,
    //           furniture: element.furniture,
    //           technology: element.technology,
    //         },
    //       })
    //     )
    //   );
    // });

    // await Promise.all(tasks);
    // console.log("added");
    // setShowLoading(false);
    // return;
    ///
    if (loadNext && lastEvaluatedKey == null) return;

    if (loadNext) console.log("load next: ", lastEvaluatedKey);
    else console.log("list objects");

    console.log("prev: ", prevItems);

    setShowLoading(true);

    try {
      const data = await ddbClient.send(
        new QueryCommand({
          TableName: TABLE_NAME_OBJECTS,
          IndexName: "global-dateEdit-index",
          KeyConditionExpression: "#global = :global",
          ExpressionAttributeNames: {
            "#global": "global",
          },
          ExpressionAttributeValues: {
            ":global": { N: "0" },
          },
          Limit: SCAN_LIMIT,
          ExclusiveStartKey: loadNext ? lastEvaluatedKey : null,
          ScanIndexForward: false,
        })
      );

      lastQueryPropertiesObjects = null;
      lastEvaluatedKeyObjects = data.LastEvaluatedKey;
      setHasMoreObjects(data.LastEvaluatedKey != null);

      console.log("lastEvaluatedKey: ", data.LastEvaluatedKey);

      var items = [];
      // var dateFrom = new Date(),
      //   dateEdit = new Date();
      if (prevItems != null) items = [...prevItems];
      data.Items.forEach(function(element, index, array) {
        // var dateFrom = new Date();
        // var dateEdit = new Date();
        // dateFrom.setTime(element.dateFrom.N);
        // dateFrom = `${dateFrom.getUTCMonth() + 1}/${dateFrom.getUTCDate() +
        //   1}/${dateFrom.getFullYear()}`;

        // dateEdit.setTime(element.dateEdit.N);
        // dateEdit = `${dateEdit.getUTCMonth() + 1}/${dateEdit.getUTCDate() +
        //   1}/${dateEdit.getFullYear()}`;
        items.push({
          id: element.id.S,
          personName: element.personName.S,
          phoneNumber: element.phoneNumber.S,
          rooms: element.rooms.N,
          dateFrom: element.dateFrom.N,
          dateEdit: element.dateEdit.N === undefined ? "0" : element.dateEdit.N,
          photoOrder: readPhotoOrder(element),
          cost: element.cost.N,
          currency: element.currency.S,
          street: element.street.S,
          region: element.region.S,
          floor: element.floor.N,
          state: element.state.S,
          heating: element.heating.S,
          info: element.info.S,
          furniture: element.furniture.BOOL,
          technology: element.technology.BOOL,
        });
      });
      setRealEstateItems(items);
      items.map((item) => {
        if (streetSearchTerms.indexOf(item.street) === -1)
          streetSearchTerms.push(item.street);
        if (regionSearchTerms.indexOf(item.region) === -1)
          regionSearchTerms.push(item.region);
      });
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setCurrentPage("Objects");
    setShowLoading(false);
  };

  const filterObjects = async (filterProps) => {
    if (ddbClient == null) {
      //alert("db client == null");
      return;
    }
    /*test
    setShowLoading(true);
    console.log("scanning...");
    const data = await ddbClient.send(
      new QueryCommand({
        TableName: "objects_v2",
        IndexName: "global-dateEdit-index",
        KeyConditionExpression: "#global = :global",
        ExpressionAttributeNames: {
          "#global": "global",
        },
        ExpressionAttributeValues: {
          ":global": { N: "0" },
        },
        Limit: 1000,
        ExclusiveStartKey: loadNext ? lastEvaluatedKey : null,
        ScanIndexForward: false,
      })
    );
    console.log("scanned: ", data.Items);
    console.log("adding...");
    var tasks = [];
    data.Items.forEach(function(element, index, array) {
      // console.log(new Date(element.dateFrom.S).getTime());
      tasks.push(
        ddbClient.send(
          new PutItemCommand({
            TableName: TABLE_NAME_OBJECTS,
            Item: {
              id: element.id,
              global: { N: "0" },
              personName: element.personName,
              phoneNumber: element.phoneNumber,
              dateFrom: { N: `${new Date(element.dateFrom.S).getTime()}` },
              dateEdit: { N: `${new Date(element.dateEdit.S).getTime()}` },
              rooms: element.rooms,
              cost: element.cost,
              currency: element.currency,
              street: element.street,
              region: element.region,
              floor: element.floor,
              state: element.state,
              heating: element.heating,
              info: element.info,
              furniture: element.furniture,
              technology: element.technology,
            },
          })
        )
      );
    });

    await Promise.all(tasks);
    console.log("added");

    console.log("scanning...");
    const data2 = await ddbClient.send(
      new QueryCommand({
        TableName: "archives",
        IndexName: "global-dateTerm-index",
        KeyConditionExpression: "#global = :global",
        ExpressionAttributeNames: {
          "#global": "global",
        },
        ExpressionAttributeValues: {
          ":global": { N: "0" },
        },
        Limit: 1000,
        ExclusiveStartKey: loadNext ? lastEvaluatedKey : null,
      })
    );
    console.log("scanned: ", data2.Items);
    console.log("adding...");
    var tasks = [];
    data2.Items.forEach(function(element, index, array) {
      tasks.push(
        ddbClient.send(
          new PutItemCommand({
            TableName: TABLE_NAME_ARCHIVES,
            Item: {
              id: element.id,
              global: { N: "0" },
              personName: element.personName,
              phoneNumber: element.phoneNumber,
              dateFrom: { N: `${new Date(element.dateFrom.S).getTime()}` },
              dateDeleted: {
                N: `${new Date(element.dateDeleted.S).getTime()}`,
              },
              dateTerm: element.dateTerm,
              dateEdit: {
                N:
                  element.dateEdit === undefined
                    ? "0"
                    : `${new Date(element.dateEdit.S).getTime()}`,
              },
              rooms: element.rooms,
              cost: element.cost,
              currency: element.currency,
              street: element.street,
              region: element.region,
              floor: element.floor,
              state: element.state,
              heating: element.heating,
              info: element.info,
              furniture: element.furniture,
              technology: element.technology,
            },
          })
        )
      );
    });

    await Promise.all(tasks);
    console.log("added");
    setShowLoading(false);
    return;
    */
    //if (loadNext && lastEvaluatedKey == null) return;

    //if (loadNext) console.log("load next: ", lastEvaluatedKey);
    //else console.log("list objects");

    //console.log("prev: ", prevItems);

    setShowLoading(true);

    try {
      // The whole table, page by page. `Limit` does not do that on its own:
      // DynamoDB caps every response at 1 MB whatever the limit says and hands
      // back a LastEvaluatedKey for the rest — reading one response filtered
      // the first ~780 listings and silently dropped the other ~400.
      var rows = [];
      var startKey = null;
      do {
        const data = await ddbClient.send(
          new QueryCommand({
            TableName: TABLE_NAME_OBJECTS,
            IndexName: "global-dateEdit-index",
            KeyConditionExpression: "#global = :global",
            ExpressionAttributeNames: {
              "#global": "global",
            },
            ExpressionAttributeValues: {
              ":global": { N: "0" },
            },
            ExclusiveStartKey: startKey,
            ScanIndexForward: false,
          })
        );
        rows = rows.concat(data.Items || []);
        startKey = data.LastEvaluatedKey || null;
      } while (startKey);

      let items = new Array();
      // -1 and "" both mean "no bound".
      const minCost = priceBound(filterProps.fromCost);
      const maxCost = priceBound(filterProps.toCost);
      // var dateFrom = new Date(),
      //   dateEdit = new Date();
      rows.forEach(function(element, index, array) {
        // var dateFrom = new Date();
        // var dateEdit = new Date();
        // dateFrom.setTime(element.dateFrom.N);
        // dateFrom = `${dateFrom.getUTCMonth() + 1}/${dateFrom.getUTCDate() +
        //   1}/${dateFrom.getFullYear()}`;

        // dateEdit.setTime(element.dateEdit.N);
        // dateEdit = `${dateEdit.getUTCMonth() + 1}/${dateEdit.getUTCDate() +
        //   1}/${dateEdit.getFullYear()}`;
        // console.log(filterProps.fromCost);
        // console.log(filterProps.toCost == -1);
        if (
          (element.street.S.toLowerCase().includes(
            filterProps.street.toLowerCase()
          ) ||
            filterProps.street.length == 0) &&
          (filterProps.rooms == -1 || filterProps.rooms == element.rooms.N) &&
          (filterProps.region.length == 0 ||
            element.region.S.toLowerCase().includes(
              filterProps.region.toLowerCase()
            )) &&
          (filterProps.heating.length == 0 ||
            element.heating.S.toLowerCase().includes(
              filterProps.heating.toLowerCase()
            )) &&
          phoneMatches(element.phoneNumber.S, filterProps.phone) &&
          // The number as stored, whatever its currency: the realtor searches
          // the way the listings are written, not in converted hryvnia.
          (minCost === null || Number(element.cost.N) >= minCost) &&
          (maxCost === null || Number(element.cost.N) <= maxCost)
        ) {
          items.push({
            id: element.id.S,
            personName: element.personName.S,
            phoneNumber: element.phoneNumber.S,
            rooms: element.rooms.N,
            dateFrom: element.dateFrom.N,
            dateEdit:
              element.dateEdit.N === undefined ? "0" : element.dateEdit.N,
            photoOrder: readPhotoOrder(element),
            cost: element.cost.N,
            currency: element.currency.S,
            street: element.street.S,
            region: element.region.S,
            floor: element.floor.N,
            state: element.state.S,
            heating: element.heating.S,
            info: element.info.S,
            furniture: element.furniture.BOOL,
            technology: element.technology.BOOL,
          });
        }
      });
      setRealEstateItems(items);
      // The scan above went through the whole table, so this IS the complete
      // result. Without clearing these, «load more» in the card view fell
      // through to listObjects and appended the plain list's next page to a
      // filtered one.
      lastQueryPropertiesObjects = null;
      lastEvaluatedKeyObjects = null;
      setHasMoreObjects(false);
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setCurrentPage("Objects");
    setShowLoading(false);
  };

  //query objects
  const queryObjects = async (
    loadNext = false,
    lastEvaluatedKey,
    street,
    rooms,
    region,
    heating,
    fromCost,
    toCost,
    prevItems
  ) => {
    setShowLoading(true);
    console.log(`loadNext: ${loadNext}`);
    try {
      if (loadNext && lastEvaluatedKey == null) {
        setShowLoading(false);
        return;
      }

      // if (!loadNext) {
      //   if (
      //     street.length === 0 &&
      //     rooms.length === 0 &&
      //     region.length === 0 &&
      //     heating.length === 0 &&
      //     fromCost.length === 0 &&
      //     toCost.length === 0
      //   ) {
      //     listObjects();
      //     return;
      //   }
      //   var keyConditionExpression = "#index = :index";
      //   var expressionAttributeNames = { "#index": "global" };
      //   var expressionAttributeValues = { ":index": { N: "0" } };
      //   var startingValue = "";
      //   var indexName = "global";

      //   if (street.length !== 0) {
      //     indexName = "street";
      //     expressionAttributeValues[":index"] = { S: `${street}` };
      //     startingValue = `${street}`;
      //   }

      //   if (rooms.length !== 0) {
      //     if (indexName === "global") {
      //       indexName = "rooms";
      //       expressionAttributeValues[":index"] = { N: `${rooms}` };
      //       startingValue = `${rooms}`;
      //     } else {
      //       indexName += "_rooms";
      //       startingValue = `${startingValue}_${rooms}`;
      //       expressionAttributeValues[":index"] = {
      //         S: startingValue,
      //       };
      //     }
      //   }

      //   if (region.length !== 0) {
      //     if (indexName === "global") {
      //       indexName = "region";
      //       expressionAttributeValues[":index"] = { S: `${region}` };
      //       startingValue = `${region}`;
      //     } else {
      //       indexName += "_region";
      //       startingValue = `${startingValue}_${region}`;
      //       expressionAttributeValues[":index"] = {
      //         S: startingValue,
      //       };
      //     }
      //   }

      //   if (heating.length !== 0) {
      //     if (indexName === "global") {
      //       indexName = "heating";
      //       expressionAttributeValues[":index"] = { S: `${heating}` };
      //       startingValue = `${heating}`;
      //     } else {
      //       indexName += "_heating";
      //       startingValue = `${startingValue}_${heating}`;
      //       expressionAttributeValues[":index"] = {
      //         S: startingValue,
      //       };
      //     }
      //   }

      //   if (indexName !== "global") {
      //     expressionAttributeNames["#index"] = indexName;
      //   }

      //   if (fromCost.length !== 0 || toCost.length !== 0) {
      //     expressionAttributeNames["#cost"] = "cost";
      //   }

      //   if (fromCost.length !== 0 && toCost.length !== 0) {
      //     keyConditionExpression += " AND #cost BETWEEN :fromCost AND :toCost";
      //     expressionAttributeValues[":fromCost"] = { N: fromCost };
      //     expressionAttributeValues[":toCost"] = { N: toCost };
      //   } else {
      //     if (fromCost.length !== 0) {
      //       keyConditionExpression += " AND #cost >= :fromCost";
      //       expressionAttributeValues[":fromCost"] = { N: fromCost };
      //     }

      //     if (toCost.length !== 0) {
      //       keyConditionExpression += " AND #cost <= :toCost";
      //       expressionAttributeValues[":toCost"] = { N: toCost };
      //     }
      //   }

      //   indexName += "-cost-index";

      //   console.log(
      //     `query: ${expressionAttributeNames["#index"]} = ${expressionAttributeValues[":index"].S}`
      //   );
      // } else {
      //   console.log("query next: ", lastEvaluatedKey);
      //   indexName = lastQueryPropertiesObjects.IndexName;
      //   keyConditionExpression =
      //     lastQueryPropertiesObjects.KeyConditionExpression;
      //   expressionAttributeValues =
      //     lastQueryPropertiesObjects.ExpressionAttributeValues;
      //   expressionAttributeNames =
      //     lastQueryPropertiesObjects.ExpressionAttributeNames;
      // }

      // var data = await ddbClient.send(
      //   new QueryCommand({
      //     TableName: TABLE_NAME_OBJECTS,
      //     IndexName: indexName,
      //     KeyConditionExpression: keyConditionExpression,
      //     ExpressionAttributeValues: expressionAttributeValues,
      //     ExpressionAttributeNames: expressionAttributeNames,
      //     Limit: SCAN_LIMIT,
      //     ExclusiveStartKey: loadNext
      //       ? lastEvaluatedKey.lastEvaluatedKey
      //       : null,
      //     // ProjectionExpression: OBJECTS_PROJECTION,
      //   })
      // );

      // lastQueryPropertiesObjects = {
      //   IndexName: indexName,
      //   KeyConditionExpression: keyConditionExpression,
      //   ExpressionAttributeValues: expressionAttributeValues,
      //   ExpressionAttributeNames: expressionAttributeNames,
      // };
      // lastEvaluatedKeyObjects = data.LastEvaluatedKey;

      if (!loadNext) {
        if (
          street.length === 0 &&
          rooms.length === 0 &&
          region.length === 0 &&
          heating.length === 0 &&
          fromCost.length === 0 &&
          toCost.length === 0
        ) {
          listObjects();
          return;
        }
        var filterExpression = "";
        var expressionAttributeNames = {
          "#global": "global",
        };
        var expressionAttributeValues = {
          ":global": { N: "0" },
        };

        if (street.length !== 0) {
          filterExpression = "contains(#street, :street)";
          expressionAttributeNames["#street"] = "street";
          expressionAttributeValues[":street"] = {
            S: `${street}`,
          };
        }

        if (rooms.length !== 0) {
          expressionAttributeNames["#rooms"] = "rooms";
          expressionAttributeValues[":rooms"] = {
            N: `${rooms}`,
          };
          if (filterExpression.length < 1) {
            filterExpression = "#rooms = :rooms";
          } else {
            filterExpression += " AND #rooms = :rooms";
          }
        }

        if (region.length !== 0) {
          expressionAttributeNames["#region"] = "region";
          expressionAttributeValues[":region"] = {
            S: region,
          };
          if (filterExpression.length < 1) {
            filterExpression = "contains(#region, :region)";
          } else {
            filterExpression += " AND contains(#region, :region)";
          }
        }

        if (heating.length !== 0) {
          expressionAttributeNames["#heating"] = "heating";
          expressionAttributeValues[":heating"] = {
            S: heating,
          };
          if (filterExpression.length < 1) {
            filterExpression = "contains(#heating, :heating)";
          } else {
            filterExpression += " AND contains(#heating, :heating)";
          }
        }

        if (fromCost.length !== 0 || toCost.length !== 0) {
          expressionAttributeNames["#cost"] = "cost";
        }

        if (fromCost.length !== 0 && toCost.length !== 0) {
          if (filterExpression.length < 1) {
            filterExpression += "#cost BETWEEN :fromCost AND :toCost";
          } else {
            filterExpression += " AND #cost BETWEEN :fromCost AND :toCost";
          }
          expressionAttributeValues[":fromCost"] = {
            N: fromCost,
          };
          expressionAttributeValues[":toCost"] = {
            N: toCost,
          };
        } else {
          if (fromCost.length !== 0) {
            if (filterExpression.length < 1) {
              filterExpression += "#cost >= :fromCost";
            } else {
              filterExpression += " AND #cost >= :fromCost";
            }
            expressionAttributeValues[":fromCost"] = {
              N: fromCost,
            };
          }

          if (toCost.length !== 0) {
            if (filterExpression.length < 1) {
              filterExpression += "#cost <= :toCost";
            } else {
              filterExpression += " AND #cost <= :toCost";
            }
            expressionAttributeValues[":toCost"] = {
              N: toCost,
            };
          }
        }

        console.log(`query filter: ${filterExpression}`);
      } else {
        console.log("query next: ", lastEvaluatedKey);
        console.log(
          `query filter: ${lastQueryPropertiesObjects.FilterExpression}`
        );
        filterExpression = lastQueryPropertiesObjects.FilterExpression;
        expressionAttributeValues =
          lastQueryPropertiesObjects.ExpressionAttributeValues;
        expressionAttributeNames =
          lastQueryPropertiesObjects.ExpressionAttributeNames;
      }

      var data = await ddbClient.send(
        new QueryCommand({
          TableName: TABLE_NAME_OBJECTS,
          IndexName: "global-dateEdit-index",
          KeyConditionExpression: "#global = :global",
          FilterExpression: filterExpression,
          ExpressionAttributeNames: expressionAttributeNames,
          ExpressionAttributeValues: expressionAttributeValues,
          Limit: SCAN_LIMIT,
          ExclusiveStartKey: loadNext ? lastEvaluatedKey : null,
          // ProjectionExpression: OBJECTS_PROJECTION,
        })
      );

      lastQueryPropertiesObjects = {
        FilterExpression: filterExpression,
        ExpressionAttributeValues: expressionAttributeValues,
        ExpressionAttributeNames: expressionAttributeNames,
      };
      lastEvaluatedKeyObjects = data.LastEvaluatedKey;
      setHasMoreObjects(data.LastEvaluatedKey != null);

      console.log("lastKey: ", data.LastEvaluatedKey);

      var items = [];
      if (prevItems != null) items = [...prevItems];
      data.Items.forEach(function(element, index, array) {
        items.push({
          id: element.id.S,
          personName: element.personName.S,
          phoneNumber: element.phoneNumber.S,
          rooms: element.rooms.N,
          dateFrom: element.dateFrom.N,
          dateEdit: element.dateEdit.N,
          // dateTo: element.dateTo.S,
          photoOrder: readPhotoOrder(element),
          cost: element.cost.N,
          currency: element.currency.S,
          street: element.street.S,
          region: element.region.S,
          floor: element.floor.N,
          state: element.state.S,
          heating: element.heating.S,
          info: element.info.S,
          photos: "", //element.photos.L
          furniture: element.furniture.BOOL,
          technology: element.technology.BOOL,
        });
      });

      if (items.length < 100 && lastEvaluatedKeyObjects != null) {
        await timeout(200);
        console.log("Waiting 200ms");
        queryObjects(
          true,
          lastEvaluatedKeyObjects,
          null,
          null,
          null,
          null,
          null,
          null,
          items
        );
        return;
      }

      console.log(items);

      setRealEstateItems(items);
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  //list clients
  const listClients = async (loadNext = false, lastEvaluatedKey, prevItems) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    if (loadNext && lastEvaluatedKey == null) return;

    if (loadNext) console.log("load next: ", lastEvaluatedKey);
    else console.log("list clients");

    setShowLoading(true);

    try {
      const data = await ddbClient.send(
        new QueryCommand({
          TableName: TABLE_NAME_CLIENTS,
          Limit: SCAN_LIMIT,
          ExclusiveStartKey: loadNext ? lastEvaluatedKey : null,
          IndexName: "global-dateEdit-index",
          KeyConditionExpression: "#global = :global",
          ExpressionAttributeNames: {
            "#global": "global",
          },
          ExpressionAttributeValues: {
            ":global": { N: "0" },
          },
          ScanIndexForward: false,
          // ProjectionExpression: CLIENTS_PROJECTION,
        })
      );

      lastQueryPropertiesClients = null;
      lastEvaluatedKeyClients = data.LastEvaluatedKey;

      var items = [];
      if (prevItems != null) items = [...prevItems];
      data.Items.forEach(function(element, index, array) {
        items.push({
          id: element.id.S,
          dateFrom: element.dateFrom.S,
          dateEdit:
            element.dateEdit === undefined
              ? new Date(element.dateFrom.S).getTime()
              : element.dateEdit.N,
          rooms: element.rooms.N,
          photoOrder: readPhotoOrder(element),
          cost: element.cost.N,
          currency: element.currency.S,
          region: element.region.S,
          aboutClient: element.aboutClient.S,
          deadline: element.deadline.N,
          urgency: element.urgency.S,
          personName: element.personName.S,
          phoneNumber: element.phoneNumber.S,
          info: element.info.S,
        });
      });
      setClientItems(items);

      items.map((item) => {
        if (clientRegionSearchTerms.indexOf(item.region) === -1) {
          clientRegionSearchTerms.push(item.region);
        }
      });
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setCurrentPage("Clients");
    setShowLoading(false);
  };

  //query clients
  const queryClients = async (
    loadNext,
    lastEvaluatedKey,
    rooms,
    region,
    fromCost,
    toCost,
    prevItems
  ) => {
    console.log(`${rooms} ${region} ${fromCost} ${toCost}`);
    setShowLoading(true);

    try {
      if (loadNext && lastEvaluatedKey == null) {
        setShowLoading(false);
        return;
      }

      if (!loadNext) {
        if (
          rooms.length === 0 &&
          region.length === 0 &&
          fromCost.length === 0 &&
          toCost.length === 0
        ) {
          listClients();
          return;
        }
        var keyConditionExpression = "#index = :index";
        var expressionAttributeNames = {
          "#index": "global",
        };
        var expressionAttributeValues = {
          ":index": { N: "0" },
        };
        var startingValue = "";
        var indexName = "global";

        if (rooms.length !== 0) {
          indexName = "rooms";
          expressionAttributeValues[":index"] = {
            N: `${rooms}`,
          };
          startingValue = `${rooms}`;
        }

        if (region.length !== 0) {
          if (indexName === "global") {
            indexName = "region";
            expressionAttributeValues[":index"] = {
              S: `${region}`,
            };
            startingValue = `${region}`;
          } else {
            indexName += "_region";
            startingValue = `${startingValue}_${region}`;
            expressionAttributeValues[":index"] = {
              S: startingValue,
            };
          }
        }

        if (indexName !== "global") {
          expressionAttributeNames["#index"] = indexName;
        }

        if (fromCost.length !== 0 || toCost.length !== 0) {
          expressionAttributeNames["#cost"] = "cost";
        }

        if (fromCost.length !== 0 && toCost.length !== 0) {
          keyConditionExpression += " AND #cost BETWEEN :fromCost AND :toCost";
          expressionAttributeValues[":fromCost"] = {
            N: fromCost,
          };
          expressionAttributeValues[":toCost"] = {
            N: toCost,
          };
        } else {
          if (fromCost.length !== 0) {
            keyConditionExpression += " AND #cost >= :fromCost";
            expressionAttributeValues[":fromCost"] = {
              N: fromCost,
            };
          }

          if (toCost.length !== 0) {
            keyConditionExpression += " AND #cost <= :toCost";
            expressionAttributeValues[":toCost"] = {
              N: toCost,
            };
          }
        }

        indexName += "-cost-index";

        console.log(toCost);
      } else {
        console.log("query next: ", lastEvaluatedKey);
        indexName = lastQueryPropertiesClients.IndexName;
        keyConditionExpression =
          lastQueryPropertiesClients.KeyConditionExpression;
        expressionAttributeValues =
          lastQueryPropertiesClients.ExpressionAttributeValues;
        expressionAttributeNames =
          lastQueryPropertiesClients.ExpressionAttributeNames;
      }

      var data = await ddbClient.send(
        new QueryCommand({
          TableName: TABLE_NAME_CLIENTS,
          IndexName: indexName,
          KeyConditionExpression: keyConditionExpression,
          ExpressionAttributeValues: expressionAttributeValues,
          ExpressionAttributeNames: expressionAttributeNames,
          Limit: SCAN_LIMIT,
          ExclusiveStartKey: loadNext ? lastEvaluatedKey : null,
          // ProjectionExpression: CLIENTS_PROJECTION,
        })
      );

      lastQueryPropertiesClients = {
        IndexName: indexName,
        KeyConditionExpression: keyConditionExpression,
        ExpressionAttributeValues: expressionAttributeValues,
        ExpressionAttributeNames: expressionAttributeNames,
      };
      lastEvaluatedKeyClients = data.LastEvaluatedKey;

      var items = [];
      if (prevItems != null) items = [...prevItems];
      data.Items.forEach(function(element, index, array) {
        items.push({
          id: element.id.S,
          dateFrom: element.dateFrom.S,
          dateEdit: element.dateEdit.N,
          rooms: element.rooms.N,
          photoOrder: readPhotoOrder(element),
          cost: element.cost.N,
          currency: element.currency.S,
          region: element.region.S,
          aboutClient: element.aboutClient.S,
          deadline: element.deadline.N,
          urgency: element.urgency.S,
          personName: element.personName.S,
          phoneNumber: element.phoneNumber.S,
          info: element.info.S,
        });
      });
      setClientItems(items);
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  //list archives
  const listArchives = async (
    loadNext = false,
    lastEvaluatedKey,
    prevItems
  ) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    if (loadNext && lastEvaluatedKey == null) {
      console.log(`loadNext: ${loadNext}`);
      console.log(lastEvaluatedKey);
      console.log(prevItems);
      return;
    }

    if (loadNext) console.log("load next: ", lastEvaluatedKey);
    else console.log("list archives");

    setShowLoading(true);

    try {
      const data = await ddbClient.send(
        new QueryCommand({
          TableName: TABLE_NAME_ARCHIVES,
          IndexName: "global-dateTerm-index",
          KeyConditionExpression: "#global = :global",
          ExpressionAttributeNames: {
            "#global": "global",
          },
          ExpressionAttributeValues: {
            ":global": { N: "0" },
          },
          Limit: SCAN_LIMIT,
          ExclusiveStartKey: loadNext ? lastEvaluatedKey : null,
        })
      );

      lastQueryPropertiesArchives = null;
      lastPhoneArchives = "";
      lastEvaluatedKeyArchives = data.LastEvaluatedKey;
      setHasMoreArchives(data.LastEvaluatedKey != null);

      console.log("lastEvaluatedKey: ", data.LastEvaluatedKey);

      var items = [];
      if (prevItems != null) items = [...prevItems];
      data.Items.forEach(function(element, index, array) {
        // var date = new Date();
        // date.setTime(element.dateTerm.N);
        // date = `${date.getUTCMonth() + 1}/${date.getUTCDate() +
        //   1}/${date.getFullYear()}`;
        items.push({
          id: element.id.S,
          personName: element.personName.S,
          phoneNumber: element.phoneNumber.S,
          rooms: element.rooms.N,
          dateFrom: element.dateFrom.N,
          dateDeleted: element.dateDeleted.N,
          dateTerm: element.dateTerm.N,
          dateEdit: element.dateEdit != undefined ? element.dateEdit.N : "",
          photoOrder: readPhotoOrder(element),
          cost: element.cost.N,
          currency: element.currency.S,
          street: element.street.S,
          region: element.region.S,
          floor: element.floor.N,
          state: element.state.S,
          heating: element.heating.S,
          info: element.info.S,
          furniture: element.furniture.BOOL,
          technology: element.technology.BOOL,
        });
      });

      if (items.length < 100 && lastEvaluatedKeyArchives != null) {
        listArchives(true, lastEvaluatedKeyArchives, items);
        return;
      }

      setArchiveItems(items);

      items.map((item) => {
        if (streetSearchTerms.indexOf(item.street) === -1)
          streetSearchTerms.push(item.street);
        if (regionSearchTerms.indexOf(item.region) === -1)
          regionSearchTerms.push(item.region);
      });
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setCurrentPage("Archives");
    setShowLoading(false);
  };

  //query archives
  const queryArchives = async (
    loadNext = false,
    lastEvaluatedKeyArchives,
    street,
    rooms,
    region,
    heating,
    fromCost,
    toCost,
    phone,
    prevItems
  ) => {
    setShowLoading(true);
    console.log(`loadNext: ${loadNext}`);
    try {
      if (loadNext && lastEvaluatedKeyArchives == null) {
        setShowLoading(false);
        return;
      }

      if (!loadNext) {
        if (
          street === null ||
          rooms === null ||
          region === null ||
          heating === null ||
          fromCost === null ||
          toCost === null ||
          phone === null
        ) {
          console.log("some of the items are null!");
          return;
        }
        if (
          street.length === 0 &&
          rooms.length === 0 &&
          region.length === 0 &&
          heating.length === 0 &&
          fromCost.length === 0 &&
          toCost.length === 0 &&
          phone.length === 0
        ) {
          listArchives();
          return;
        }
        var filterExpression = "";
        var expressionAttributeNames = {
          "#global": "global",
        };
        var expressionAttributeValues = {
          ":global": { N: "0" },
        };

        if (street.length !== 0) {
          filterExpression = "contains(#street, :street)";
          expressionAttributeNames["#street"] = "street";
          expressionAttributeValues[":street"] = {
            S: `${street}`,
          };
        }

        if (rooms.length !== 0) {
          expressionAttributeNames["#rooms"] = "rooms";
          expressionAttributeValues[":rooms"] = {
            N: `${rooms}`,
          };
          if (filterExpression.length < 1) {
            filterExpression = "#rooms = :rooms";
          } else {
            filterExpression += " AND #rooms = :rooms";
          }
        }

        if (region.length !== 0) {
          expressionAttributeNames["#region"] = "region";
          expressionAttributeValues[":region"] = {
            S: region,
          };
          if (filterExpression.length < 1) {
            filterExpression = "contains(#region, :region)";
          } else {
            filterExpression += " AND contains(#region, :region)";
          }
        }

        if (heating.length !== 0) {
          expressionAttributeNames["#heating"] = "heating";
          expressionAttributeValues[":heating"] = {
            S: heating,
          };
          if (filterExpression.length < 1) {
            filterExpression = "contains(#heating, :heating)";
          } else {
            filterExpression += " AND contains(#heating, :heating)";
          }
        }

        // The stored number as-is, whatever the currency — the same
        // comparison the objects list makes.
        if (fromCost.length !== 0 || toCost.length !== 0) {
          expressionAttributeNames["#cost"] = "cost";
        }

        if (fromCost.length !== 0 && toCost.length !== 0) {
          if (filterExpression.length < 1) {
            filterExpression += "#cost BETWEEN :fromCost AND :toCost";
          } else {
            filterExpression += " AND #cost BETWEEN :fromCost AND :toCost";
          }
          expressionAttributeValues[":fromCost"] = {
            N: fromCost,
          };
          expressionAttributeValues[":toCost"] = {
            N: toCost,
          };
        } else {
          if (fromCost.length !== 0) {
            if (filterExpression.length < 1) {
              filterExpression += "#cost >= :fromCost";
            } else {
              filterExpression += " AND #cost >= :fromCost";
            }
            expressionAttributeValues[":fromCost"] = {
              N: fromCost,
            };
          }

          if (toCost.length !== 0) {
            if (filterExpression.length < 1) {
              filterExpression += "#cost <= :toCost";
            } else {
              filterExpression += " AND #cost <= :toCost";
            }
            expressionAttributeValues[":toCost"] = {
              N: toCost,
            };
          }
        }

        lastPhoneArchives = phone;

        console.log(`query filter: ${filterExpression}`);
      } else {
        console.log("query next: ", lastEvaluatedKeyArchives);
        console.log(
          `query filter: ${lastQueryPropertiesArchives.FilterExpression}`
        );
        filterExpression = lastQueryPropertiesArchives.FilterExpression;
        expressionAttributeValues =
          lastQueryPropertiesArchives.ExpressionAttributeValues;
        expressionAttributeNames =
          lastQueryPropertiesArchives.ExpressionAttributeNames;
      }

      var data = await ddbClient.send(
        new QueryCommand({
          TableName: TABLE_NAME_ARCHIVES,
          IndexName: "global-dateTerm-index",
          KeyConditionExpression: "#global = :global",
          // A phone-only search has nothing for DynamoDB to filter (the phone
          // is matched in the browser), and an empty expression is rejected.
          ...(filterExpression ? { FilterExpression: filterExpression } : {}),
          ExpressionAttributeNames: expressionAttributeNames,
          ExpressionAttributeValues: expressionAttributeValues,
          Limit: SCAN_LIMIT,
          ExclusiveStartKey: loadNext ? lastEvaluatedKeyArchives : null,
          // ProjectionExpression: OBJECTS_PROJECTION,
        })
      );

      lastQueryPropertiesArchives = {
        FilterExpression: filterExpression,
        ExpressionAttributeValues: expressionAttributeValues,
        ExpressionAttributeNames: expressionAttributeNames,
      };
      lastEvaluatedKeyArchives = data.LastEvaluatedKey;
      setHasMoreArchives(data.LastEvaluatedKey != null);

      console.log("lastKey: ", data.LastEvaluatedKey);

      var items = [];
      if (prevItems != null) items = [...prevItems];
      data.Items.forEach(function(element, index, array) {
        // Matched here, not in the FilterExpression — see lastPhoneArchives.
        if (!phoneMatches(element.phoneNumber.S, lastPhoneArchives)) return;
        // var date = new Date();
        // date.setTime(element.dateTerm.N);
        // date = `${date.getUTCMonth() + 1}/${date.getUTCDate() +
        //   1}/${date.getFullYear()}`;
        items.push({
          id: element.id.S,
          personName: element.personName.S,
          phoneNumber: element.phoneNumber.S,
          rooms: element.rooms.N,
          dateFrom: element.dateFrom.N,
          dateDeleted: element.dateDeleted.N,
          dateTerm: element.dateTerm.N,
          dateEdit: element.dateEdit != undefined ? element.dateEdit.N : "0",
          photoOrder: readPhotoOrder(element),
          cost: element.cost.N,
          currency: element.currency.S,
          street: element.street.S,
          region: element.region.S,
          floor: element.floor.N,
          state: element.state.S,
          heating: element.heating.S,
          info: element.info.S,
          furniture: element.furniture.BOOL,
          technology: element.technology.BOOL,
        });
      });

      if (items.length < 100 && lastEvaluatedKeyArchives != null) {
        await timeout(200);
        console.log("Waiting 200ms");
        queryArchives(
          true,
          lastEvaluatedKeyArchives,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          items
        );
        return;
      }

      console.log(items);

      setArchiveItems(items);
    } catch (err) {
      alert(err);
      console.error(err);
      // await timeout(1000); //wait for 1 sec
      // console.log("Trying again");
      // queryArchives(
      //   loadNext,
      //   lastEvaluatedKeyArchives,
      //   street,
      //   rooms,
      //   region,
      //   heating,
      //   fromCost,
      //   toCost,
      //   items
      // );
    }
    setShowLoading(false);
  };

  const loadNextObjects = async () => {
    if (lastQueryPropertiesObjects == null) {
      // No key means no next page; listObjects would start over from page one.
      if (lastEvaluatedKeyObjects == null) return;
      listObjects(true, lastEvaluatedKeyObjects, realEstateItems);
    } else {
      console.log("query OBJECTS");
      queryObjects(
        true,
        lastEvaluatedKeyObjects,
        null,
        null,
        null,
        null,
        null,
        null,
        realEstateItems
      );
    }
  };

  const loadNextClients = async () => {
    if (lastQueryPropertiesClients == null) {
      listClients(true, lastEvaluatedKeyClients, clientItems);
    } else {
      queryClients(
        true,
        lastEvaluatedKeyClients,
        null,
        null,
        null,
        null,
        null,
        clientItems
      );
    }
  };

  const loadNextArchives = async () => {
    if (lastQueryPropertiesArchives == null) {
      if (lastEvaluatedKeyArchives == null) return;
      listArchives(true, lastEvaluatedKeyArchives, archiveItems);
    } else {
      console.log("query ARCHIVES");
      queryArchives(
        true,
        lastEvaluatedKeyArchives,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        archiveItems
      );
    }
  };

  //add real estate object
  const addObjectItem = async (item, inputFiles) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    setShowLoading(true);
    setShowAddRealEstateObject(false);

    // The dialogs build their item out of form fields only; the order lives on
    // the row already, so read it back before the Put overwrites the row.
    const existing = findListing(item.id);
    const keptOrder =
      item.photoOrder || (existing ? existing.photoOrder : null) || [];

    try {
      console.log(item);

      await ddbClient.send(
        new PutItemCommand({
          TableName: TABLE_NAME_OBJECTS,
          Item: {
            id: { S: `${item.id}` },
            global: { N: "0" },
            personName: { S: item.personName },
            phoneNumber: { S: item.phoneNumber },
            dateFrom: { N: `${item.dateFrom}` },
            dateEdit: { N: `${item.dateEdit}` },
            rooms: { N: `${item.rooms}` },
            cost: { N: `${item.cost}` },
            currency: { S: item.currency },
            street: {
              S: item.street,
            },
            region: { S: item.region },
            floor: { N: `${item.floor}` },
            state: { S: item.state },
            heating: {
              S: item.heating,
            },
            info: { S: item.info },
            furniture: { BOOL: item.furniture },
            technology: { BOOL: item.technology },
            // This function saves edits as well as new listings, and a Put
            // replaces the whole row — without this, editing any field would
            // silently reset the photo order the realtor arranged.
            ...(photoOrderAttribute(keptOrder)
              ? { photoOrder: photoOrderAttribute(keptOrder) }
              : {}),
          },
        })
      );
      var items = realEstateItems.filter(
        (estateItem) => estateItem.id !== item.id
      );
      // Front, not back: every list here is queried with
      // ScanIndexForward:false, i.e. newest dateEdit first. Appending put a
      // just-saved listing at the end of the 100 rows already loaded, so it
      // looked like nothing had been added until you hit «Знайти» or reloaded.
      items.unshift({ ...item, photoOrder: keptOrder });
      setRealEstateItems(items);
      console.log("add success");

      await uploadPhotos(item.id, inputFiles);

      /**
       * Refresh this listing's social-media preview page while we are here.
       *
       * Without it, a link to a brand-new listing shows the site logo in
       * Telegram and Viber until someone remembers to run
       * scripts/generate_og_pages.py — and nothing anywhere says so.
       *
       * Deliberately after the save and deliberately swallowed: a preview is
       * worth far less than the realtor's work, so a missing permission or a
       * network blip must not turn a successful save into an error dialog.
       */
      try {
        // With keptOrder, not the dialog's item: the Edit dialog builds its item
        // from form fields only, so an edit republished the preview with the
        // alphabetically-first photo instead of the cover the realtor chose.
        await publishOgPage(s3Client, { ...item, photoOrder: keptOrder });
      } catch (ogErr) {
        // Two different things have to be in place, and they fail differently:
        //   TypeError: Failed to fetch  -> CORS on the leocity.estate bucket
        //   AccessDenied                -> the IAM statement
        // Naming only one of them sends you looking in the wrong place.
        console.warn(
          "Оголошення збережено, але сторінку прев'ю не оновлено.\n" +
            "1) CORS бакета leocity.estate має дозволяти PUT з " +
            "https://leocity.estate — якщо в консолі 'Failed to fetch' " +
            "або 'Access-Control-Allow-Origin', причина саме тут;\n" +
            "2) ролі рієлтора потрібен s3:PutObject на leocity.estate/og/* — " +
            "якщо в консолі AccessDenied.\n" +
            "Обидва пункти описані в SHARE_PREVIEW.md, розділ 5.",
          ogErr
        );
      }
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  //add client
  const addClientItem = async (item) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    setShowLoading(true);
    setShowAddClients(false);

    try {
      console.log(item.id);
      console.log(item.personName);

      await ddbClient.send(
        new PutItemCommand({
          TableName: TABLE_NAME_CLIENTS,
          Item: {
            id: { S: `${item.id}` },
            global: { N: "0" },
            dateFrom: { S: item.dateFrom },
            dateEdit: { N: `${item.dateEdit}` },
            rooms: { N: `${item.rooms}` },
            cost: { N: `${item.cost}` },
            currency: { S: item.currency },
            region: { S: item.region },
            aboutClient: { S: item.aboutClient },
            deadline: { N: `${item.deadline}` },
            urgency: { S: item.urgency },
            personName: { S: item.personName },
            phoneNumber: { S: item.phoneNumber },
            info: { S: item.info },
            rooms_region: {
              S: `${item.rooms}_${item.region}`,
            },
          },
        })
      );
      var items = clientItems.filter((clientItem) => clientItem.id !== item.id);
      // Front, not back: every list here is queried with
      // ScanIndexForward:false, i.e. newest dateEdit first. Appending put a
      // just-saved listing at the end of the 100 rows already loaded, so it
      // looked like nothing had been added until you hit «Знайти» or reloaded.
      items.unshift(item);
      setClientItems(items);

      // setClientItems([...clientItems, item]);
      console.log("add success");
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  //add item to archive
  const addArchiveItem = async (item) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    setShowLoading(true);

    try {
      console.log(item);

      await ddbClient.send(
        new PutItemCommand({
          TableName: TABLE_NAME_ARCHIVES,
          Item: {
            id: { S: `${item.id}` },
            global: { N: "0" },
            personName: { S: item.personName },
            phoneNumber: { S: item.phoneNumber },
            dateFrom: { N: `${item.dateFrom}` },
            dateDeleted: { N: `${item.dateDeleted}` },
            dateTerm: { N: `${item.dateTerm}` },
            dateEdit: { N: `${item.dateEdit}` },
            rooms: { N: `${item.rooms}` },
            cost: { N: `${item.cost}` },
            currency: { S: item.currency },
            street: {
              S: item.street,
            },
            region: { S: item.region },
            floor: { N: `${item.floor}` },
            state: { S: item.state },
            heating: {
              S: item.heating,
            },
            info: { S: item.info },
            furniture: { BOOL: item.furniture },
            technology: { BOOL: item.technology },
            // Archiving and returning copy the whole row; the photo order has
            // to ride along or a listing loses its cover on the way there.
            ...(photoOrderAttribute(item.photoOrder)
              ? { photoOrder: photoOrderAttribute(item.photoOrder) }
              : {}),
          },
        })
      );
      var items = archiveItems.filter(
        (archiveItem) => archiveItem.id !== item.id
      );
      // Front, not back: every list here is queried with
      // ScanIndexForward:false, i.e. newest dateEdit first. Appending put a
      // just-saved listing at the end of the 100 rows already loaded, so it
      // looked like nothing had been added until you hit «Знайти» or reloaded.
      items.unshift(item);
      setArchiveItems(items);
      // setRealEstateItems([...realEstateItems, item]);
      console.log("add success");
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  //add item to archive
  const onEditArchiveItem = async (item, inputFiles) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    setShowLoading(true);

    // The dialog builds its item out of form fields only; the order lives on
    // the row already, so read it back before the Put overwrites the row.
    const existing = findListing(item.id);
    const keptOrder =
      item.photoOrder || (existing ? existing.photoOrder : null) || [];

    try {
      console.log(item);

      await ddbClient.send(
        new PutItemCommand({
          TableName: TABLE_NAME_ARCHIVES,
          Item: {
            id: { S: `${item.id}` },
            global: { N: "0" },
            personName: { S: item.personName },
            phoneNumber: { S: item.phoneNumber },
            dateFrom: { N: `${item.dateFrom}` },
            dateDeleted: { N: `${item.dateDeleted}` },
            dateTerm: { N: `${item.dateTerm}` },
            rooms: { N: `${item.rooms}` },
            cost: { N: `${item.cost}` },
            currency: { S: item.currency },
            street: {
              S: item.street,
            },
            region: { S: item.region },
            floor: { N: `${item.floor}` },
            state: { S: item.state },
            heating: {
              S: item.heating,
            },
            info: { S: item.info },
            furniture: { BOOL: item.furniture },
            technology: { BOOL: item.technology },
            // A Put replaces the whole row — without this, editing any field
            // would silently reset the photo order the realtor arranged.
            ...(photoOrderAttribute(keptOrder)
              ? { photoOrder: photoOrderAttribute(keptOrder) }
              : {}),
          },
        })
      );
      var items = archiveItems.filter(
        (archiveItem) => archiveItem.id !== item.id
      );
      // Front, not back: every list here is queried with
      // ScanIndexForward:false, i.e. newest dateEdit first. Appending put a
      // just-saved listing at the end of the 100 rows already loaded, so it
      // looked like nothing had been added until you hit «Знайти» or reloaded.
      items.unshift({ ...item, photoOrder: keptOrder });
      setArchiveItems(items);
      console.log("add success");

      await uploadPhotos(item.id, inputFiles);
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  //add item to archive
  const addDeletedArchiveItem = async (item) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    setShowLoading(true);

    try {
      console.log(item);

      await ddbClient.send(
        new PutItemCommand({
          TableName: TABLE_NAME_DELETED_ARCHIVE,
          Item: {
            id: { S: `${item.id}` },
            global: { N: "0" },
            personName: { S: item.personName },
            phoneNumber: { S: item.phoneNumber },
            dateFrom: { N: `${item.dateFrom}` },
            dateDeleted: { N: `${item.dateDeleted}` },
            dateTerm: { N: `${item.dateTerm}` },
            dateEdit: { N: `${item.dateEdit}` },
            rooms: { N: `${item.rooms}` },
            cost: { N: `${item.cost}` },
            currency: { S: item.currency },
            street: {
              S: item.street,
            },
            region: { S: item.region },
            floor: { N: `${item.floor}` },
            state: { S: item.state },
            heating: {
              S: item.heating,
            },
            info: { S: item.info },
            furniture: { BOOL: item.furniture },
            technology: { BOOL: item.technology },
          },
        })
      );
      // setRealEstateItems([...realEstateItems, item]);
      console.log("add success");
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  const beforeDeleteObject = (id) => {
    setActiveItemId(id);
    setOpenDeleteDialog(true);
    return;
  };

  const beforeReturnObject = (id) => {
    setActiveItemId(id);
    setOpenReturnDialog(true);
    return;
  };

  const beforeDeleteArchive = (id) => {
    setActiveItemId(id);
    setOpenDeleteArchiveDialog(true);
    return;
  };

  const returnItem = async (id) => {
    //console.log(item);
    archiveItems
      .filter((item) => item.id === id)
      .map((item) => {
        item.dateEdit = startOfToday();
        addObjectItem(item, null);
      });
    removeFromArchive(id);
  };

  const removeFromArchive = async (id) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    setShowLoading(true);

    try {
      await ddbClient.send(
        new DeleteItemCommand({
          TableName: TABLE_NAME_ARCHIVES,
          Key: {
            id: { S: id },
          },
        })
      );
      setArchiveItems(
        archiveItems.filter((archiveItem) => archiveItem.id !== id)
      );
      console.log("delete success");
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  const deleteArchive = async (id) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    setShowLoading(true);

    archiveItems.map((item) => {
      if (item.id === id) addDeletedArchiveItem(item);
    });

    try {
      await ddbClient.send(
        new DeleteItemCommand({
          TableName: TABLE_NAME_ARCHIVES,
          Key: {
            id: { S: id },
          },
        })
      );
      setArchiveItems(
        archiveItems.filter((archiveItem) => archiveItem.id !== id)
      );
      console.log("delete success");

      // Both the originals and the thumbnails tree.
      const prefixes = albumPrefixes(id);
      var objects = [];

      for (var p = 0; p < prefixes.length; p++) {
        const photosData = await s3Client.send(
          new ListObjectsCommand({
            Bucket: S3_PHOTOS_BUCKET,
            Prefix: prefixes[p],
          })
        );
        if (photosData.Contents != null) {
          objects = objects.concat(
            photosData.Contents.map(function(object) {
              return { Key: object.Key };
            })
          );
        }
      }

      if (objects.length > 0) {
        await s3Client.send(
          new DeleteObjectsCommand({
            Bucket: S3_PHOTOS_BUCKET,
            Delete: { Objects: objects },
            Quiet: true,
          })
        );

        console.log("photos deleted");
      }

      invalidateAlbum(id);
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  //delete real estate object
  const deleteItem = async (id, date) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    setShowLoading(true);

    realEstateItems
      .filter((item) => item.id === id)
      .map((item) => {
        item.dateDeleted = startOfToday();
        item.dateTerm = dayStart(date.displayDateFrom);
        item.dateTo = "";
        addArchiveItem(item);
      });

    try {
      await ddbClient.send(
        new DeleteItemCommand({
          TableName: TABLE_NAME_OBJECTS,
          Key: {
            id: { S: id },
          },
        })
      );
      setRealEstateItems(
        realEstateItems.filter((realEstateItem) => realEstateItem.id !== id)
      );
      console.log("delete success");

      // const albumKey = encodeURIComponent(id) + "/";

      // const photosData = await s3Client.send(
      //   new ListObjectsCommand({
      //     Bucket: S3_PHOTOS_BUCKET,
      //     Prefix: albumKey,
      //   })
      // );

      // if (photosData.Contents != null) {
      //   const objects = photosData.Contents.map(function (object) {
      //     return { Key: object.Key };
      //   });

      //   await s3Client.send(
      //     new DeleteObjectsCommand({
      //       Bucket: S3_PHOTOS_BUCKET,
      //       Delete: { Objects: objects },
      //       Quiet: true,
      //     })
      //   );

      //   console.log("photos deleted");
      // }
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  //delete client object
  const deleteClientItem = async (id) => {
    if (ddbClient == null) {
      // alert("db client == null");
      return;
    }

    setShowLoading(true);

    try {
      await ddbClient.send(
        new DeleteItemCommand({
          TableName: TABLE_NAME_CLIENTS,
          Key: {
            id: { S: id },
          },
        })
      );
      setClientItems(clientItems.filter((clientItem) => clientItem.id !== id));
      console.log("delete success");
    } catch (err) {
      alert(err);
      console.error(err);
    }
    setShowLoading(false);
  };

  useEffect(() => {
    const ac = new AbortController();
    if (ddbClient == null) {
      return;
    }
    if (!displayAdminTools) setDisplayAdminTools(true);

    if (admin == null) {
      checkAdmin();
    }

    return () => ac.abort();
  }, []);

  const openViewPage = async (rowValues) => {
    window.open(`/kvartyra/${rowValues.row.id}`, "_blank");
  };

  // ddbClient == null ||
  //     admin == null ||
  //     (window.location.pathname !== "/" &&
  //       window.location.pathname !== "/Clients" &&
  //       window.location.pathname !== "/Archives") ? (
  //     <div>
  //       {console.log(`Other page: ${window.location.pathname}`)}
  //       <HashRouter>
  //         <Routes>
  //           <Route path="/View" element={<AppClient />}></Route>
  //         </Routes>
  //       </HashRouter>
  //     </div>) : (
  return ddbClient == null || admin == null ? (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        fontFamily: '"Manrope", "Inter", sans-serif',
        color: HALLOWEEN || CHRISTMAS ? "rgba(255,255,255,0.72)" : "#5B6B85",
        background: HALLOWEEN
          ? "linear-gradient(180deg, #07061A 0%, #120D2A 55%, #2A1650 100%)"
          : CHRISTMAS
          ? "linear-gradient(180deg, #011A42 0%, #02275E 60%, #0A2C66 100%)"
          : "#F6F8FC",
      }}
    >
      {HALLOWEEN ? (
        <div className="hw-float" style={{ width: 64 }}>
          <Pumpkin style={{ display: "block", width: "100%", height: "auto" }} />
        </div>
      ) : CHRISTMAS ? (
        <div className="xm-float">
          <Sprite atlas={ICONS} name="snowman" scale={0.7} />
        </div>
      ) : (
        <div
          style={{
            width: 34,
            height: 34,
            border: "3px solid #DDEBFD",
            borderTopColor: "#1470E0",
            borderRadius: "50%",
            animation: "leocity-spin 0.9s linear infinite",
          }}
        />
      )}
      <div>Завантаження панелі рієлтора…</div>
    </div>
  ) : (
    <div style={{ minHeight: "100vh", background: "#F6F8FC" }}>
      {/* Bats and a peeking ghost; the spider here lets clicks through. */}
      {HALLOWEEN && <HalloweenOverlay interactive={false} />}
      {/* Lighter than the public site's fall: this is where the work happens. */}
      {CHRISTMAS && <Snowfall density={0.45} />}
      <BuildingInfo
        handleCloseInfo={() => setOpenInfo(false)}
        openInfo={openInfo}
        infoText={infoText}
      />
      <StandardImageList
        handleClosePhotos={() => setOpenPhotos(false)}
        openPhotos={openPhotos}
        photos={photos}
        onDeletePhoto={onDeletePhoto}
        onReorder={(next) => reorderPhotos(photosId, next)}
        admin={admin}
        // Downloads go through the signed client, not the CDN — see the
        // download() comment in StandardImageList.
        s3Client={s3Client}
      />
      {displayAdminTools && (
        <ResponsiveAppBar
          listClients={listClients}
          listObjects={listObjects}
          listArchives={listArchives}
          openWatermark={() => setCurrentPage("Watermark")}
          admin={admin}
          // The maintenance switch writes site_config with these authenticated
          // credentials — never the public site's anonymous ones.
          ddbClient={ddbClient}
        />
      )}
      <BeforeDeleteObject
        openDialog={openDeleteDialog}
        id={activeItemId}
        deleteObject={deleteItem}
        handleCloseDialog={() => setOpenDeleteDialog(false)}
      />
      <BeforeDeleteArchive
        openDialog={openDeleteArchiveDialog}
        id={activeItemId}
        deleteObject={deleteArchive}
        handleCloseDialog={() => setOpenDeleteArchiveDialog(false)}
      />
      <BeforeReturnArchive
        openDialog={openReturnDialog}
        id={activeItemId}
        returnItem={returnItem}
        handleCloseDialog={() => setOpenReturnDialog(false)}
      />
      {currentPage === "Objects" && (
        <RealEstateObjectsPage
          onAddItem={() => setShowAddRealEstateObject(true)}
          addItem={addObjectItem}
          showLoading={showLoading}
          showAddRealEstateObject={showAddRealEstateObject}
          onCloseDialog={() => setShowAddRealEstateObject(false)}
          onCloseEditDialog={() => setIsEditItem(false)}
          realEstateItems={realEstateItems}
          items={realEstateItems}
          deleteItem={deleteItem}
          handleOpenInfo={handleOpenInfo}
          handleOpenPhotos={handleOpenPhotos}
          handleEditItem={handleEditItem}
          handleDeleteItem={beforeDeleteObject}
          queryObjects={filterObjects}
          listObjects={listObjects}
          streetSearchTerms={streetSearchTerms}
          regionSearchTerms={regionSearchTerms}
          isEditItem={isEditItem}
          item={editItem}
          onPageChanged={onTablePageChange}
          loadNextObjects={loadNextObjects}
          hasMoreObjects={hasMoreObjects}
          // The card view lists each listing's album to find its cover photo,
          // and does it with the panel's own authenticated client.
          s3Client={s3Client}
          admin={admin}
          openViewPage={openViewPage}
          albumPhotos={editPhotos}
          albumLoading={editPhotosLoading}
          onReorderAlbum={(next) => reorderPhotos(editItemId, next)}
          onDeleteAlbumPhoto={(photo) => onDeletePhoto(photo, true)}
        />
      )}
      {currentPage === "Clients" && (
        <ClientsPage
          onAddItem={() => setShowAddClients(true)}
          addItem={addClientItem}
          showLoading={showLoading}
          showAddClient={showAddClients}
          onCloseDialog={() => setShowAddClients(false)}
          onCloseEditDialog={() => setIsEditItem(false)}
          clientItems={clientItems}
          items={clientItems}
          deleteItem={deleteClientItem}
          handleOpenInfo={handleOpenClientInfo}
          handleEditItem={handleEditClientItem}
          handleDeleteItem={deleteClientItem}
          queryClients={queryClients}
          regionSearchTerms={clientRegionSearchTerms}
          isEditItem={isEditItem}
          item={editClientItem}
          loadNextClients={loadNextClients}
          admin={admin}
        />
      )}
      {/* Purely local: reads photos from a folder the realtor picks and writes
          them back. No DynamoDB, no S3, so it needs no clients. */}
      {currentPage === "Watermark" && <WatermarkPage />}
      {currentPage === "Archives" && (
        <ArchivesPage
          release={!PRODUCTION}
          items={archiveItems}
          showLoading={showLoading}
          deleteItem={beforeDeleteArchive}
          openArchiveInfo={openArchiveInfo}
          handleOpenPhotos={handleOpenPhotos}
          handleEditItem={handleEditArchiveItem}
          returnItem={beforeReturnObject}
          loadNextArchives={loadNextArchives}
          hasMore={hasMoreArchives}
          openViewPage={openViewPage}
          editArchiveItem={editArchiveItem}
          onEditItem={onEditArchiveItem}
          isEditItem={isEditItem}
          regionSearchTerms={regionSearchTerms}
          streetSearchTerms={streetSearchTerms}
          onCloseEditDialog={() => setIsEditItem(false)}
          queryObjects={queryArchives}
          s3Client={s3Client}
          albumPhotos={editPhotos}
          albumLoading={editPhotosLoading}
          onReorderAlbum={(next) => reorderPhotos(editItemId, next)}
          onDeleteAlbumPhoto={onDeletePhoto}
        />
      )}
    </div>
  );
}

export default AppRealtor;
